import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import cx from 'classnames';
import { useEffect, useState } from 'react';

import { IconChevronDown } from '../../icons/ChevronDown';
import { Typography } from '../../typography/Typography';

import style from './MultiSelect.module.css';

export interface Option {
    label: string;
    value: string;
}

type MultiSelectProps = {
    label: string;
    options: Option[];
    selectedOptions: Option[];
    onChange: (options: Option[]) => void;
    disabled?: boolean;
    disabledOptions?: boolean;
    onMenuToggle?: (isOpen: boolean) => void;
    sortOptionsBySelected?: boolean;
    slotProps?: {
        button?: {
            className?: string;
        };
    };
};

const sortOptions = (options: Option[], value: Option[]) => {
    return [...options].sort((a, b) => {
        const aSelected = value.some((item) => item.value === a.value);
        const bSelected = value.some((item) => item.value === b.value);

        if (aSelected === bSelected) return 0;
        return aSelected ? -1 : 1;
    });
};

export const MultiSelect = ({
    label,
    options,
    selectedOptions,
    onChange,
    disabled = false,
    disabledOptions = false,
    onMenuToggle,
    sortOptionsBySelected = false,
    slotProps,
}: MultiSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [displayedOptions, setDisplayedOptions] = useState(options);

    useEffect(() => {
        if (sortOptionsBySelected && !isOpen) {
            setDisplayedOptions(sortOptions(options, selectedOptions));
        } else if (!sortOptionsBySelected) {
            setDisplayedOptions(options);
        }
    }, [options, selectedOptions, sortOptionsBySelected, isOpen]);

    const handleOpenChange = (newIsOpen: boolean) => {
        setIsOpen(newIsOpen);
        onMenuToggle?.(newIsOpen);
    };

    const handleOptionSelect = (option: Option) => {
        if (disabled || disabledOptions) return;

        const isSelected = selectedOptions.some((item) => item.value === option.value);

        if (isSelected) {
            onChange(selectedOptions.filter((item) => item.value !== option.value));
            return;
        }

        onChange([...selectedOptions, option]);
    };

    const displayValue =
        selectedOptions.length === 0 ? label : `${label} (${selectedOptions.length})`;

    return (
        <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
            <DropdownMenu.Trigger
                className={cx(
                    style.button,
                    style.withIconRight,
                    { [style.open]: isOpen },
                    slotProps?.button?.className,
                )}
                disabled={disabled}
            >
                <Typography scale="primary-body-bold" className={style.buttonLabel}>
                    {displayValue}
                </Typography>
                <IconChevronDown className={style.arrow} />
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className={style.dropdown}
                    side="bottom"
                    sideOffset={4}
                    align="start"
                    avoidCollisions
                    collisionPadding={12}
                >
                    {displayedOptions.map((option) => {
                        const isSelected = selectedOptions.some(
                            (item) => item.value === option.value,
                        );
                        return (
                            <DropdownMenu.Item
                                key={option.value}
                                className={cx(style.option, {
                                    [style.selected]: isSelected,
                                    [style.optionDisabled]: disabledOptions,
                                })}
                                onSelect={(event) => {
                                    event.preventDefault();
                                    handleOptionSelect(option);
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={disabled || disabledOptions}
                                    className={style.checkbox}
                                />
                                <span>{option.label}</span>
                            </DropdownMenu.Item>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};
